import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import type { HubInteraction, HubAttachment, HubContext } from '../types/hub';

export const HUB_CLIENT_THREAD_KEY = 'client';

/** Optional admin-only RPC args (see `get_hub_context` / `get_hub_interactions` migrations). */
export type HubRpcAdminOptions = {
  /** When set for an administrator, hub data is loaded as this profile (e.g. principal freelancer). */
  adminEffectiveViewerId?: string | null;
};

interface HubState {
  context: HubContext | null;
  contextLoading: boolean;
  contextError: string | null;
  /** Interactions stored per participant key (one key per thread). */
  interactionsByParticipantKey: Record<string, HubInteraction[]>;
  /** Initial load state per participant. */
  loadingByParticipantKey: Record<string, boolean>;
  /** Load-more state per participant. */
  loadingMoreByParticipantKey: Record<string, boolean>;
  /** Pagination flag per participant. */
  hasMoreByParticipantKey: Record<string, boolean>;
  error: string | null;
  pageSize: number;
  /** Unread hub message count per participant thread (keyed by quote_id or HUB_CLIENT_THREAD_KEY). */
  hubUnreadCountByParticipantKey: Record<string, number>;
  hubUnreadCountsLoading: boolean;
  fetchHubContext: (jobId: string, options?: HubRpcAdminOptions) => Promise<HubContext | null>;
  fetchHubUnreadCounts: (jobId: string, options?: HubRpcAdminOptions) => Promise<void>;
  fetchHubInteractions: (
    jobId: string,
    participantKey: string,
    participantId: string,
    beforeCreatedAt?: string | null,
    options?: HubRpcAdminOptions
  ) => Promise<HubInteraction[]>;
  loadMoreInteractions: (
    jobId: string,
    participantKey: string,
    participantId: string,
    options?: HubRpcAdminOptions
  ) => Promise<void>;
  sendHubMessage: (
    jobId: string,
    recipientIds: string[],
    message: string,
    attachments: HubAttachment[],
    quoteId?: string | null
  ) => Promise<void>;
  uploadHubAttachment: (
    jobId: string,
    file: File,
    quoteId?: string | null
  ) => Promise<HubAttachment>;
  /** Reset interactions for a given participant, or all if key is omitted. */
  resetInteractions: (participantKey?: string) => void;
  /** Clear hub context (e.g. when leaving admin hub read-only view). */
  clearHubContext: () => void;
}

/** Monotonic id so overlapping fetchHubContext calls only clear loading for the latest request. */
let hubContextFetchSeq = 0;

export const useHubStore = create<HubState>((set, get) => ({
  context: null,
  contextLoading: false,
  contextError: null,
  interactionsByParticipantKey: {},
  loadingByParticipantKey: {},
  loadingMoreByParticipantKey: {},
  hasMoreByParticipantKey: {},
  error: null,
  pageSize: 10,
  hubUnreadCountByParticipantKey: {},
  hubUnreadCountsLoading: false,

  fetchHubContext: async (jobId: string, options?: HubRpcAdminOptions) => {
    const seq = ++hubContextFetchSeq;
    set({ contextLoading: true, contextError: null });
    try {
      const { data, error } = await supabase.rpc('get_hub_context', {
        p_job_id: jobId,
        p_admin_effective_viewer_id: options?.adminEffectiveViewerId ?? null,
      });

      if (error) throw error;

      const context = data as HubContext;
      if (seq === hubContextFetchSeq) {
        set({ context, contextError: null });
      }
      return context;
    } catch (error) {
      if (seq === hubContextFetchSeq) {
        set({ contextError: (error as Error).message });
      }
      return null;
    } finally {
      if (seq === hubContextFetchSeq) {
        set({ contextLoading: false });
      }
    }
  },

  clearHubContext: () => {
    set({
      context: null,
      contextError: null,
      contextLoading: false,
    });
  },

  resetInteractions: (participantKey?: string) => {
    if (!participantKey) {
      set({
        interactionsByParticipantKey: {},
        hasMoreByParticipantKey: {},
        loadingByParticipantKey: {},
        loadingMoreByParticipantKey: {},
      });
      return;
    }

    set(state => {
      const {
        interactionsByParticipantKey,
        hasMoreByParticipantKey,
        loadingByParticipantKey,
        loadingMoreByParticipantKey,
      } = state;

      // Create shallow copies so Zustand change detection works properly.
      const newInteractions = { ...interactionsByParticipantKey };
      const newHasMore = { ...hasMoreByParticipantKey };
      const newLoading = { ...loadingByParticipantKey };
      const newLoadingMore = { ...loadingMoreByParticipantKey };

      delete newInteractions[participantKey];
      delete newHasMore[participantKey];
      delete newLoading[participantKey];
      delete newLoadingMore[participantKey];

      return {
        interactionsByParticipantKey: newInteractions,
        hasMoreByParticipantKey: newHasMore,
        loadingByParticipantKey: newLoading,
        loadingMoreByParticipantKey: newLoadingMore,
      };
    });
  },

  fetchHubUnreadCounts: async (jobId: string, options?: HubRpcAdminOptions) => {
    if (get().hubUnreadCountsLoading) return;

    set({ hubUnreadCountsLoading: true });
    try {
      const { data, error } = await supabase.rpc('get_hub_unread_counts', {
        p_job_id: jobId,
        p_admin_effective_viewer_id: options?.adminEffectiveViewerId ?? null,
      });
      if (error) throw error;

      const byKey: Record<string, number> = {};
      for (const row of data ?? []) {
        const r = row as {
          quote_id?: string | null;
          freelancer_id?: string | null;
          unread_count: number;
        };
        const key =
          r.quote_id ??
          (r.freelancer_id != null && r.freelancer_id !== ''
            ? `request:${r.freelancer_id}`
            : HUB_CLIENT_THREAD_KEY);
        byKey[key] = Number(r.unread_count) || 0;
      }

      set({ hubUnreadCountByParticipantKey: byKey });
    } catch {
      set({ hubUnreadCountByParticipantKey: {} });
    } finally {
      set({ hubUnreadCountsLoading: false });
    }
  },

  fetchHubInteractions: async (jobId, participantKey, participantId, beforeCreatedAt, options) => {
    const isLoadMore = Boolean(beforeCreatedAt);

    if (!isLoadMore) {
      const alreadyLoading = get().loadingByParticipantKey[participantKey] ?? false;
      if (alreadyLoading) return [];
    }

    set(state => ({
      error: null,
      loadingByParticipantKey: {
        ...state.loadingByParticipantKey,
        ...(isLoadMore ? {} : { [participantKey]: true }),
      },
      loadingMoreByParticipantKey: {
        ...state.loadingMoreByParticipantKey,
        ...(isLoadMore ? { [participantKey]: true } : {}),
      },
    }));

    try {
      const { data, error } = await supabase.rpc('get_hub_interactions', {
        p_job_id: jobId,
        p_participant_id: participantId,
        p_limit: get().pageSize,
        p_before_created_at: beforeCreatedAt || null,
        p_admin_effective_viewer_id: options?.adminEffectiveViewerId ?? null,
      });

      if (error) {
        console.error('Error fetching interactions:', error);
        throw error;
      }

      const interactions = (data ?? []) as HubInteraction[];

      set(state => {
        const existing = state.interactionsByParticipantKey[participantKey] ?? [];

        let updatedInteractions: HubInteraction[];
        if (isLoadMore) {
          const existingIds = new Set(existing.map(i => i.id));
          const uniqueNew = interactions.filter(i => !existingIds.has(i.id));
          updatedInteractions = [...existing, ...uniqueNew];
        } else {
          updatedInteractions = interactions;
        }

        return {
          error: null,
          interactionsByParticipantKey: {
            ...state.interactionsByParticipantKey,
            [participantKey]: updatedInteractions,
          },
          hasMoreByParticipantKey: {
            ...state.hasMoreByParticipantKey,
            [participantKey]: interactions.length === get().pageSize,
          },
        };
      });

      return interactions;
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    } finally {
      set(state => ({
        loadingByParticipantKey: {
          ...state.loadingByParticipantKey,
          [participantKey]: false,
        },
        loadingMoreByParticipantKey: {
          ...state.loadingMoreByParticipantKey,
          [participantKey]: false,
        },
      }));
    }
  },

  loadMoreInteractions: async (
    jobId: string,
    participantKey: string,
    participantId: string | null,
    options?: HubRpcAdminOptions
  ) => {
    const { interactionsByParticipantKey, loadingMoreByParticipantKey, hasMoreByParticipantKey } =
      get();

    const interactions = interactionsByParticipantKey[participantKey] ?? [];
    const loadingMore = loadingMoreByParticipantKey[participantKey] ?? false;
    const hasMore = hasMoreByParticipantKey[participantKey] ?? true;

    if (loadingMore || !hasMore || interactions.length === 0 || participantId == null) return;

    const oldestCreatedAt = interactions[interactions.length - 1]?.created_at;
    if (!oldestCreatedAt) return;

    set(state => ({
      error: null,
      loadingMoreByParticipantKey: {
        ...state.loadingMoreByParticipantKey,
        [participantKey]: true,
      },
    }));

    try {
      const { data, error } = await supabase.rpc('get_hub_interactions', {
        p_job_id: jobId,
        p_participant_id: participantId,
        p_limit: get().pageSize,
        p_before_created_at: oldestCreatedAt,
        p_admin_effective_viewer_id: options?.adminEffectiveViewerId ?? null,
      });

      if (error) throw error;

      const newInteractions = (data ?? []) as HubInteraction[];

      set(state => {
        const existing = state.interactionsByParticipantKey[participantKey] ?? [];
        const existingIds = new Set(existing.map(i => i.id));
        const uniqueNew = newInteractions.filter(i => !existingIds.has(i.id));

        return {
          interactionsByParticipantKey: {
            ...state.interactionsByParticipantKey,
            [participantKey]: [...existing, ...uniqueNew],
          },
          hasMoreByParticipantKey: {
            ...state.hasMoreByParticipantKey,
            [participantKey]: newInteractions.length === get().pageSize,
          },
          error: null,
        };
      });
    } catch (error) {
      set({ error: (error as Error).message });
    } finally {
      set(state => ({
        loadingMoreByParticipantKey: {
          ...state.loadingMoreByParticipantKey,
          [participantKey]: false,
        },
      }));
    }
  },

  sendHubMessage: async (
    jobId: string,
    recipientIds: string[],
    message: string,
    attachments: HubAttachment[],
    quoteId?: string | null
  ) => {
    set({ error: null });
    try {
      const { error } = await supabase.rpc('send_hub_message', {
        p_job_id: jobId,
        p_quote_id: quoteId || null,
        p_message: message,
        p_recipient_ids: recipientIds,
        p_attachments: attachments,
      });

      if (error) throw error;
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    }
  },

  uploadHubAttachment: async (
    jobId: string,
    file: File,
    quoteId?: string | null
  ): Promise<HubAttachment> => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session?.access_token) {
      throw new Error('Not authenticated');
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('job_id', jobId);
    if (quoteId) {
      formData.append('quote_id', quoteId);
    }

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/upload-hub-attachment`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
        body: formData,
      }
    );

    const result = await response.json();
    if (!response.ok) {
      throw new Error(result.error || 'Failed to upload file');
    }

    return result as HubAttachment;
  },
}));
